import RestX from 'highered-utils/restx';
import AuthService from './AuthService';
import UniversityService from './UniversityService';
import { subDomain } from '~/utils/urlUtils';
import uuidv4 from '~/utils/uuidv4';

// The dimensions set up in Google Analytics
const EVENT_UNIQUE_ID = 'event_id';
const USER_ID_DIMENSION = 'user_id';
const SCHOOL_ID_DIMENSION = 'school_id';
const ATTRIBUTE1_DIMENSION = 'attribute_1';
const ATTRIBUTE2_DIMENSION = 'attribute_2';
const ATTRIBUTE3_DIMENSION = 'attribute_3';
const ATTRIBUTE4_DIMENSION = 'attribute_4';

// Custom dimensions configuration for gtag('config', ...)
const GA_DIMENSION_MAP = {
    dimension1: EVENT_UNIQUE_ID,
    dimension2: USER_ID_DIMENSION,
    dimension3: SCHOOL_ID_DIMENSION,
    dimension4: ATTRIBUTE1_DIMENSION,
    dimension5: ATTRIBUTE2_DIMENSION,
    dimension6: ATTRIBUTE3_DIMENSION,
    dimension7: ATTRIBUTE4_DIMENSION,
};

// Event Actions
const APPLY_CLICKED_ACTION = 'apply_click';
const JOBAD_VIEW_ACTION = 'jobad_view';
const COMPANY_VIEW_ACTION = 'company_view';
const USER_LOGIN_ACTION = 'user_logged_in';
const USER_VERIFIED_ACTION = 'user_verified';
const ARTICLE_VIEW_ACTION = 'article_view';
const VIDEO_VIEW_ACTION = 'video_view';
const RESOURCE_VIEW_ACTION = 'resource_view';
const CHAT_CLICK_ACTION = 'chat_click';

// Event categories
const JOBAD_CATEGORY = 'jobads';
const CONTENT_CATEGORY = 'company_content';

// GA attributes
const EVENT_CATEGORY = 'event_category';
const EVENT_LABEL = 'event_label';
const PAGE_TITLE = 'page_title';
const PAGE_PATH = 'page_path';
const CUSTOM_MAP = 'custom_map';
const COOKIE_DOMAIN = 'cookie_domain';
const CLIENT_ID = 'ga_client_id';

const GA_DEFAULT_CONFIG = {
    [CUSTOM_MAP]: GA_DIMENSION_MAP,
    [COOKIE_DOMAIN]: 'auto',
};

// Ingestion API configurations
const INGESTION_API_URI = process.env.VUE_APP_ANALYTICS_INGESTION_API;
const INGESTION_METADATA_JOBAD_ID = 'job_id';
const INGESTION_METADATA_SCHOOL_ID = 'school_id';
const INGESTION_METADATA_COMPANY_ID = 'company_id';
const INGESTION_METADATA_ARTICLE_ID = 'article_id';
const INGESTION_METADATA_VIDEO_ID = 'video_id';

class AnalyticService {
    static clientId = '';

    // Initialize initializes the AnalyticService dimension-mappings.
    static initialize() {
        if (!gtag) {
            return;
        }
        gtag('config', process.env.VUE_APP_GA_TRACKING_ID, GA_DEFAULT_CONFIG);
        this.getClientId(); // Initializes the anonymous client id
    }

    static getClientId() {
        // Get client id if it already exists
        if (this.clientId) {
            return this.clientId;
        }

        // Get client id from local storage
        let clientId = localStorage.getItem(CLIENT_ID);
        if (!clientId) {
            // Generate new clientId if it is not in localstorage
            this.clientId = uuidv4();
            localStorage.setItem(CLIENT_ID, this.clientId);
            clientId = this.clientId;
        }
        return clientId;
    }

    // logPageview sends a pageview-log to Google-Analytics with a given pagePath
    static logPageview(pagePath) {
        // Validate input
        if (!gtag || !pagePath) {
            return;
        }

        gtag('config', process.env.VUE_APP_GA_TRACKING_ID, {
            [PAGE_TITLE]: document.title,
            [PAGE_PATH]: pagePath,
            ...GA_DEFAULT_CONFIG,
        });
    }

    // SendEvent sends an event by given name and with given data
    static async sendEvent(eventAction, category, data, metadata) {
        let isAnonymous = false;

        // Include userId in event
        const userId = await AuthService.getUserId();
        if (userId !== null) {
            data[USER_ID_DIMENSION] = userId;
        } else {
            // If not logged in, send anonymous client-id instead
            const clientId = this.getClientId();
            data[USER_ID_DIMENSION] = `anonymous_${clientId}`;
            isAnonymous = true;
        }

        // Include schoolId in event
        const schoolData = await UniversityService.getCurrentSchoolData();
        if (schoolData) {
            data[SCHOOL_ID_DIMENSION] = schoolData.id;
        }

        // Send event
        // Check if gtag is initialized
        if (gtag) {
            gtag('event', eventAction, {
                [EVENT_UNIQUE_ID]: uuidv4(),
                [USER_ID_DIMENSION]: '',
                [ATTRIBUTE1_DIMENSION]: 'NULL',
                [ATTRIBUTE2_DIMENSION]: 'NULL',
                [ATTRIBUTE3_DIMENSION]: 'NULL',
                [ATTRIBUTE4_DIMENSION]: 'NULL',
                ...data,
                [EVENT_CATEGORY]: category,
                [EVENT_LABEL]: ''.concat(
                    UniversityService.getCurrentDomain(),
                    ' - ',
                    data[EVENT_LABEL] || ''
                ),
            });
        }

        // Build payload for ingestion API
        metadata[INGESTION_METADATA_SCHOOL_ID] = schoolData.id;
        const eventPayload = {
            user_id: data[USER_ID_DIMENSION],
            anonymous: isAnonymous,
            event_type: eventAction,
            timestamp: new Date().toISOString(),
            metadata: metadata,
        };

        fetch(INGESTION_API_URI, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(eventPayload),
        });
    }

    static applyClickEvent(jobAdId, companyId, jobTitle = null) {
        // Validate jobAd-id
        if (!jobAdId) {
            return;
        }

        const data = {
            [ATTRIBUTE1_DIMENSION]: jobAdId,
        };

        const metadata = {
            [INGESTION_METADATA_JOBAD_ID]: jobAdId,
            [INGESTION_METADATA_COMPANY_ID]: companyId,
        };

        // Add companyId and jobtitle to the event label
        if (companyId || jobTitle) {
            data[EVENT_LABEL] = `[CompanyId]${companyId} - [Job]${jobTitle || ''}`;
        }

        // Send event
        this.sendEvent(APPLY_CLICKED_ACTION, JOBAD_CATEGORY, data, metadata);
    }

    static jobadViewEvent(jobAdId, companyId, jobTitle = null) {
        // Validate jobAd-id
        if (!jobAdId) {
            return;
        }

        const data = {
            [ATTRIBUTE1_DIMENSION]: jobAdId,
        };

        const metadata = {
            [INGESTION_METADATA_JOBAD_ID]: jobAdId,
            [INGESTION_METADATA_COMPANY_ID]: companyId,
        };

        // Add companyId and jobtitle to the event label
        if (companyId || jobTitle) {
            data[EVENT_LABEL] = `[CompanyId]${companyId} - [Job]${jobTitle || ''}`;
        }

        // Send event
        this.sendEvent(JOBAD_VIEW_ACTION, JOBAD_CATEGORY, data, metadata);
    }

    static companyViewEvent(companyId, companyTitle = null) {
        // Validate companyId
        if (!companyId) {
            return;
        }
        // Initialize the event data to send
        const data = {
            [ATTRIBUTE1_DIMENSION]: companyId,
        };

        const metadata = {
            [INGESTION_METADATA_COMPANY_ID]: companyId,
        };

        // Add company title to the event label
        if (companyTitle) {
            data[EVENT_LABEL] = `[CompanyId]${companyId} - [Company]${companyTitle}`;
        }

        // Send event
        this.sendEvent(COMPANY_VIEW_ACTION, JOBAD_CATEGORY, data, metadata);
    }

    static articleViewEvent(articleId, companyId, articleTitle = null) {
        // Validate articleId and companyId
        if (!articleId || !companyId) {
            return;
        }
        // Initialize the event data to send
        const data = {
            [ATTRIBUTE1_DIMENSION]: articleId,
            [ATTRIBUTE2_DIMENSION]: companyId,
        };

        const metadata = {
            [INGESTION_METADATA_ARTICLE_ID]: articleId,
            [INGESTION_METADATA_COMPANY_ID]: companyId,
        };

        if (articleTitle) {
            data[EVENT_LABEL] = `[CompanyId]${companyId} - [Article]${articleTitle}`;
        }

        // Send the event
        this.sendEvent(ARTICLE_VIEW_ACTION, CONTENT_CATEGORY, data, metadata);
    }

    static videoViewEvent(videoId, companyId, videoTitle = null) {
        // Validate videoId and companyId
        if (!videoId || !companyId) {
            return;
        }
        // Initialize the event data to send
        const data = {
            [ATTRIBUTE1_DIMENSION]: videoId,
            [ATTRIBUTE2_DIMENSION]: companyId,
        };

        const metadata = {
            [INGESTION_METADATA_VIDEO_ID]: videoId,
            [INGESTION_METADATA_COMPANY_ID]: companyId,
        };

        if (videoTitle) {
            data[EVENT_LABEL] = `[CompanyId]${companyId} - [Video]${videoTitle}`;
        }

        // Send the event
        this.sendEvent(VIDEO_VIEW_ACTION, CONTENT_CATEGORY, data, metadata);
    }

    static chatStartEvent(companyId, companyTitle = null) {
        // Validate companyId
        if (!companyId) {
            return;
        }
        // Initialize the event data to send
        const data = {
            [ATTRIBUTE1_DIMENSION]: companyId,
        };

        const metadata = {
            [INGESTION_METADATA_COMPANY_ID]: companyId,
        };

        // Add company title to the event label
        if (companyTitle) {
            data[EVENT_LABEL] = `[CompanyId]${companyId} - [Company]${companyTitle}`;
        }

        // Send event
        this.sendEvent(CHAT_CLICK_ACTION, CONTENT_CATEGORY, data, metadata);
    }
}

export default AnalyticService;
